import { Button, Flex, Skeleton, Text } from '@tradener/lumen';

import useColorMode from '~/hooks/useColorMode';

import NineBoxCell from './NineBoxCell';
import {
  ACTIVE_CELL_STYLE,
  CELL_HEIGHT,
  EMPTY_CELL,
  EMPTY_TOTAL_CELL,
  resolveRatingBadgeBackground,
  resolveTableDividerColor,
  resolveTableRatingColumnBackground,
  resolveToneStyleByCell,
  resolveTotalLabelSurfaceBackground,
  resolveTotalLabelTextColor,
} from './constants';
import { NineBoxMetricDataProps, NineBoxRatingProps, NineBoxYearProps } from './mockData';
import { resolveCellOpacity, resolveRatingOpacity } from './selection';
import { NineBoxCellSelectionProps } from './types';

export const resolveRatingLabel = (rating: NineBoxRatingProps) => (rating === 'TOTAL' ? 'Total' : rating);

interface NineBoxSkeletonRowProps {
  rating: NineBoxRatingProps;
  years: NineBoxYearProps[];
  tableGridTemplateColumns: string;
  lastYearIndex: number;
  lastBodyRating: NineBoxRatingProps;
}

export function NineBoxSkeletonRow({
  rating,
  years,
  tableGridTemplateColumns,
  lastYearIndex,
  lastBodyRating,
}: NineBoxSkeletonRowProps) {
  const { mode } = useColorMode();
  const isTotal = rating === 'TOTAL';
  const shouldShowBottomBorder = !isTotal && rating !== lastBodyRating;
  const toneStyleByCell = resolveToneStyleByCell(mode);
  const dividerColor = resolveTableDividerColor(mode);
  const ratingColumnBackground = resolveTableRatingColumnBackground(mode);

  return (
    <Flex key={`skeleton-row-${rating}`} direction="column" width="full" data-testid="ninebox-skeleton-row">
      <Flex
        display="grid"
        gridTemplateColumns={tableGridTemplateColumns}
        minH={CELL_HEIGHT}
        bg={isTotal ? toneStyleByCell.neutral.bg : 'transparent'}
        data-testid="ninebox-skeleton"
      >
        <Flex
          px="3"
          align="center"
          bg={isTotal ? toneStyleByCell.neutral.bg : ratingColumnBackground}
          borderRight="1px solid"
          borderColor={dividerColor}
          borderBottom={shouldShowBottomBorder ? '1px solid' : undefined}
          borderBottomColor={dividerColor}
        >
          <Skeleton isLoaded={false} height="0.85rem" width="2.75rem" borderRadius="sm" />
        </Flex>

        {years.map((year, yearIndex) => (
          <Flex
            key={`skeleton-cell-${rating}-${year}`}
            px="3"
            align="center"
            justify="center"
            borderRight={yearIndex === lastYearIndex ? undefined : '1px solid'}
            borderBottom={shouldShowBottomBorder ? '1px solid' : undefined}
            borderColor={dividerColor}
          >
            <Skeleton isLoaded={false} height="0.85rem" width="3.5rem" borderRadius="sm" />
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
}

interface NineBoxRowProps {
  rating: NineBoxRatingProps;
  years: NineBoxYearProps[];
  metricData: NineBoxMetricDataProps;
  selectedCell?: NineBoxCellSelectionProps;
  selectedRating?: NineBoxRatingProps;
  tableGridTemplateColumns: string;
  lastYearIndex: number;
  lastBodyRating: NineBoxRatingProps;
  hasSelectedCell: boolean;
  hasRatingOnlyFilter: boolean;
  hasData: boolean;
  isLoading: boolean;
  onRatingSelect: (rating: NineBoxRatingProps) => void;
  onCellSelect: (selection: NineBoxCellSelectionProps) => void;
}

export function NineBoxRow({
  rating,
  years,
  metricData,
  selectedCell,
  selectedRating,
  tableGridTemplateColumns,
  lastYearIndex,
  lastBodyRating,
  hasSelectedCell,
  hasRatingOnlyFilter,
  hasData,
  isLoading,
  onRatingSelect,
  onCellSelect,
}: NineBoxRowProps) {
  const { mode } = useColorMode();
  const isTotal = rating === 'TOTAL';
  const shouldShowBottomBorder = !isTotal && rating !== lastBodyRating;
  const totalLabelBackgroundColor = resolveTotalLabelSurfaceBackground(mode);
  const totalTextColor = resolveTotalLabelTextColor(mode);
  const toneStyleByCell = resolveToneStyleByCell(mode);
  const dividerColor = resolveTableDividerColor(mode);
  const ratingColumnBackground = resolveTableRatingColumnBackground(mode);

  const isRatingActive = !selectedCell && selectedRating === rating;
  const isRatingOnSelectedAxis = selectedCell?.rating === rating;
  const ratingLabel = resolveRatingLabel(rating);

  const ratingOpacity = resolveRatingOpacity({
    hasSelectedCell,
    hasRatingOnlyFilter,
    isRatingOnSelectedAxis,
    isRatingActive,
  });

  return (
    <Flex key={`row-${rating}`} direction="column" width="full">
      <Flex display="grid" gridTemplateColumns={tableGridTemplateColumns} minH={CELL_HEIGHT} bg="transparent">
        {isTotal ? (
          <Flex
            px="3"
            py="0"
            minH={CELL_HEIGHT}
            height="100%"
            align="center"
            justify="flex-start"
            bg={totalLabelBackgroundColor}
            borderRight="1px solid"
            borderColor={dividerColor}
            borderBottom={shouldShowBottomBorder ? '1px solid' : undefined}
            borderBottomColor={dividerColor}
            opacity={ratingOpacity}
          >
            <Text textStyle="body1-semibold" color={totalTextColor} lineHeight="1">
              {ratingLabel}
            </Text>
          </Flex>
        ) : (
          <Button
            variant="unstyled"
            px="3"
            py="0"
            borderRadius="0"
            minH={CELL_HEIGHT}
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            bg={ratingColumnBackground}
            borderRight="1px solid"
            borderColor={dividerColor}
            borderBottom={shouldShowBottomBorder ? '1px solid' : undefined}
            borderBottomColor={dividerColor}
            cursor={isLoading ? 'default' : 'pointer'}
            aria-label={`Filtrar rating ${ratingLabel}`}
            aria-pressed={isRatingActive}
            title={`Filtrar por rating ${ratingLabel}`}
            boxShadow={isRatingActive ? ACTIVE_CELL_STYLE : 'none'}
            opacity={ratingOpacity}
            transform="translateY(0)"
            transition="box-shadow .2s ease, filter .2s ease, transform .2s ease, opacity .2s ease"
            _hover={{
              filter: isLoading || isRatingActive ? undefined : 'brightness(1.06)',
              transform: isLoading ? undefined : 'translateY(-1px)',
            }}
            _focusVisible={{
              outline: 'none',
            }}
            onClick={() => {
              if (isLoading) {
                return;
              }

              onRatingSelect(rating);
            }}
          >
            <Flex
              borderRadius="sm"
              alignItems="center"
              justifyContent="center"
              width="2.75rem"
              minW="2.75rem"
              height="1.7rem"
              px="0"
              bg={resolveRatingBadgeBackground(rating)}
            >
              <Text textStyle="caption1-medium" color="white" lineHeight="1">
                {ratingLabel}
              </Text>
            </Flex>
          </Button>
        )}

        {years.map((year, yearIndex) => {
          const cell = metricData[rating]?.[year] || (rating === 'TOTAL' ? EMPTY_TOTAL_CELL : EMPTY_CELL);
          const cellStyle = toneStyleByCell[cell.tone];

          const isActive = selectedCell?.rating === rating && selectedCell?.year === year;
          const isLastColumn = yearIndex === lastYearIndex;
          const isInteractive = hasData;
          const isOnSelectedAxis = selectedCell?.rating === rating || selectedCell?.year === year;
          const isOnSelectedRating = selectedRating === rating;

          const cellOpacity = resolveCellOpacity({
            hasSelectedCell,
            hasRatingOnlyFilter,
            isActive,
            isOnSelectedAxis,
            isOnSelectedRating,
          });

          return (
            <NineBoxCell
              key={`cell-${rating}-${year}`}
              rating={rating}
              ratingLabel={ratingLabel}
              year={year}
              cell={cell}
              cellStyle={cellStyle}
              dividerColor={dividerColor}
              isLastColumn={isLastColumn}
              shouldShowBottomBorder={shouldShowBottomBorder}
              isActive={isActive}
              isInteractive={isInteractive}
              cellOpacity={cellOpacity}
              onCellSelect={onCellSelect}
            />
          );
        })}
      </Flex>
    </Flex>
  );
}

export const NineBoxDataRow = NineBoxRow;
